import { CartLinesUpdateEvent } from '@shopify/events';

class PpdProductBundle extends HTMLElement {
  constructor() {
    super();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.form = this.querySelector('[data-ppd-bundle-form]');
    this.currentItem = this.querySelector('[data-ppd-current-item]');
    this.submitButton = this.querySelector('[data-ppd-bundle-submit]');
    this.submitLabel = this.querySelector('[data-ppd-bundle-submit-label]');
    this.countElement = this.querySelector('[data-ppd-bundle-count]');
    this.totalElement = this.querySelector('[data-ppd-bundle-total]');
    this.selectAllButton = this.querySelector('[data-ppd-bundle-select-all]');
    this.statusElement = this.querySelector('[data-ppd-bundle-status]');
    this.currentProductAvailable = !this.submitButton?.disabled;

    if (!this.form || !this.currentItem || !this.submitButton) return;

    this.form.addEventListener('submit', this.handleSubmit, { signal });
    this.form.addEventListener('change', this.handleChange, { signal });
    this.selectAllButton?.addEventListener('click', this.handleSelectAll, { signal });
    this.updateSummary();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get selectedAccessoryInputs() {
    return Array.from(this.querySelectorAll('[data-ppd-bundle-item]:checked:not(:disabled)'));
  }

  get selectableExtraInputs() {
    return Array.from(this.querySelectorAll('[data-ppd-bundle-item][type="checkbox"]:not(:disabled)'));
  }

  handleChange() {
    this.updateSummary();
  }

  handleSelectAll() {
    for (const input of this.selectableExtraInputs) input.checked = true;
    this.updateSummary();
  }

  getCurrentVariantId() {
    const mainVariantInput = document.querySelector(
      '.product-information product-form-component form[action*="/cart/add"] input[name="id"]'
    );
    return mainVariantInput?.value || this.currentItem.dataset.variantId;
  }

  selectedItems() {
    const currentVariantId = String(this.getCurrentVariantId() || '').split('/').pop();
    const currentItem = {
      id: currentVariantId,
      merchandiseId: 'gid://shopify/ProductVariant/' + currentVariantId,
      price: Number(this.currentItem.dataset.price) || 0,
      quantity: Math.max(1, Number(this.currentItem.dataset.quantity) || 1)
    };

    const accessories = this.selectedAccessoryInputs.map((input) => ({
      id: String(input.value || '').split('/').pop(),
      merchandiseId: input.dataset.merchandiseId,
      price: Number(input.dataset.price) || 0,
      quantity: Math.max(1, Number(input.dataset.quantity) || 1)
    }));

    return [currentItem, ...accessories].filter((item) => item.id);
  }

  formatMoney(cents) {
    const locale = this.dataset.locale || document.documentElement.lang || 'en';
    const currency = this.dataset.currency || 'USD';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(cents / 100);
  }

  updateSummary() {
    if (!this.currentItem || !this.submitButton) return;
    const items = this.selectedItems();
    const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const allExtrasSelected = this.selectableExtraInputs.every((input) => input.checked);

    if (this.countElement) this.countElement.textContent = String(items.length);
    if (this.totalElement) this.totalElement.textContent = this.formatMoney(total);
    if (this.submitLabel) {
      this.submitLabel.textContent =
        'Add ' + items.length + ' ' + (items.length === 1 ? 'product' : 'products') + ' · ' + this.formatMoney(total);
    }
    if (this.selectAllButton) {
      this.selectAllButton.hidden = this.selectableExtraInputs.length === 0 || allExtrasSelected;
    }
    this.submitButton.disabled = this.busy || !this.currentProductAvailable || items.length === 0;
  }

  setBusy(busy) {
    this.busy = busy;
    this.submitButton.toggleAttribute('aria-busy', busy);
    if (busy && this.submitLabel) this.submitLabel.textContent = 'Adding selected products…';
    if (!busy) this.updateSummary();
  }

  async fetchCart() {
    const response = await fetch(this.dataset.cartUrl, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to refresh the cart.');
    return response.json();
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.busy) return;

    const items = this.selectedItems();
    if (!items.length) return;

    const deferredEvent = CartLinesUpdateEvent.createPromise();
    const lines = items.map((item) => ({
      merchandiseId: item.merchandiseId,
      quantity: item.quantity
    }));
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferredEvent.promise
      })
    );

    this.setBusy(true);
    if (this.statusElement) {
      this.statusElement.setAttribute('role', 'status');
      this.statusElement.textContent = '';
    }

    let eventSettled = false;
    try {
      const payload = {
        items: items.map((item) => ({ id: item.id, quantity: item.quantity })),
        sections_url: window.location.pathname
      };
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (!response.ok) {
        const currentCart = await this.fetchCart().catch(() => null);
        if (currentCart) {
          deferredEvent.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
            detail: {
              items: currentCart.items,
              source: 'ppd-product-bundle',
              sourceId: this.id,
              itemCount,
              productId: this.dataset.productId,
              sections: result.sections,
              didError: true
            }
          });
          eventSettled = true;
        }
        throw new Error(result.description || result.message || 'The selected products could not be added.');
      }

      const currentCart = await this.fetchCart();
      deferredEvent.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
        detail: {
          items: currentCart.items,
          source: 'ppd-product-bundle',
          sourceId: this.id,
          itemCount,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false
        }
      });
      eventSettled = true;

      if (this.statusElement) {
        this.statusElement.textContent =
          items.length + ' ' + (items.length === 1 ? 'product was' : 'products were') + ' added to your cart.';
      }
    } catch (error) {
      if (!eventSettled) deferredEvent.reject?.(error);
      if (this.statusElement) {
        this.statusElement.setAttribute('role', 'alert');
        this.statusElement.textContent = error instanceof Error ? error.message : 'The selected products could not be added.';
      }
    } finally {
      this.setBusy(false);
    }
  }
}

if (!customElements.get('ppd-product-bundle')) {
  customElements.define('ppd-product-bundle', PpdProductBundle);
}
